import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import jsPDF from 'jspdf';
import {
    User, Mail, Phone, MapPin, Calendar, CreditCard,
    Download, Eye, ExternalLink, Loader, X, Users,
    ShieldCheck, ShieldAlert, RefreshCw, CheckCircle
} from 'lucide-react';

const Account = () => {
    const { user } = useAuth();
    const [accountData, setAccountData] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deactivatingSubscription, setDeactivatingSubscription] = useState(null);
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [transactionDetails, setTransactionDetails] = useState(null);
    const [loadingTransactionDetails, setLoadingTransactionDetails] = useState(false);
    const [environment] = useState('sandbox'); // Default to sandbox, could be made dynamic later

    // Email verification state
    const [verifyCode, setVerifyCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);
    const [resending, setResending] = useState(false);
    const [resendResult, setResendResult] = useState(null);

    useEffect(() => {
        fetchAccountData();
    }, []);

    const fetchAccountData = async () => {
        try {
            setLoading(true);
            
            // Fetch user account data, subscriptions, and transactions
            const [accountResponse, subscriptionsResponse, transactionsResponse] = await Promise.all([
                api.get('/users/my/account'),
                api.get('/users/my/subscriptions'),
                api.get('/users/my/transactions')
            ]);

            if (accountResponse.data.success) {
                setAccountData(accountResponse.data.data);
            }
            
            if (subscriptionsResponse.data.success) {
                setSubscriptions(subscriptionsResponse.data.data);
            }
            
            if (transactionsResponse.data.success) {
                setTransactions(transactionsResponse.data.data);
            }
        } catch (error) {
            console.error('Error fetching account data:', error);
            setError('Failed to load account data');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    const formatCurrency = (cents) => {
        return (cents / 100).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
        });
    };

    const handleDeactivateSubscription = async (subscriptionId) => {
        if (!window.confirm('Are you sure you want to cancel this subscription? This action cannot be undone.')) {
            return;
        }

        try {
            setDeactivatingSubscription(subscriptionId);
            
            const response = await api.post(`/users/my/subscriptions/${subscriptionId}/cancel`);
            
            if (response.data.success) {
                // Refresh subscription data
                const subscriptionsResponse = await api.get('/users/my/subscriptions');
                if (subscriptionsResponse.data.success) {
                    setSubscriptions(subscriptionsResponse.data.data);
                }
                
                // Show success message
                alert('Subscription cancelled successfully. You will continue to have access until the end of your billing period.');
            } else {
                alert('Failed to cancel subscription. Please try again.');
            }
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            alert('Failed to cancel subscription. Please contact support if the issue persists.');
        } finally {
            setDeactivatingSubscription(null);
        }
    };

    const handleViewTransactionDetails = async (transaction) => {
        setSelectedTransaction(transaction);
        setShowTransactionModal(true);
        setLoadingTransactionDetails(true);
        
        // Use the transaction data we already have instead of fetching from admin endpoint
        setTimeout(() => {
            setTransactionDetails({
                id: transaction.id,
                square_payment_id: transaction.square_payment_id,
                amount: transaction.amount,
                currency: transaction.currency,
                status: transaction.status,
                payment_method: transaction.payment_method,
                receipt_url: transaction.receipt_url,
                cardholder_name: transaction.cardholder_name || null,
                plan_name: transaction.plan_name,
                processed_at: transaction.processed_at || null,
                created_at: transaction.created_at
            });
            setLoadingTransactionDetails(false);
        }, 100);
    };

    const handleDownloadInvoice = (transaction) => {
        const invoiceData = {
            invoiceNumber: `INV-${transaction.id}`,
            date: formatDate(transaction.created_at),
            customerName: user?.profile?.displayName || user?.profile?.username || 'Customer',
            customerEmail: user?.email,
            cardholderName: transaction.cardholder_name || null,
            description: transaction.plan_name || 'Payment',
            amount: formatCurrency(transaction.amount),
            status: transaction.status,
            paymentId: transaction.square_payment_id
        };

        // Create new PDF document
        const pdf = new jsPDF();
        
        // Set font sizes and colors
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(20);
        pdf.setTextColor(41, 128, 185); // Blue color
        
        // Header
        pdf.text('THE DEEP WAVE', 20, 30);
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0); // Black
        pdf.text('Arts & Sound Ensemble', 20, 40);
        
        // Invoice title
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(24);
        pdf.text('INVOICE', 150, 30);
        
        // Invoice details
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(12);
        pdf.text(`Invoice Number: ${invoiceData.invoiceNumber}`, 150, 45);
        pdf.text(`Date: ${invoiceData.date}`, 150, 55);
        
        // Bill To section
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.text('BILL TO:', 20, 80);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(12);
        pdf.text(invoiceData.customerName, 20, 95);
        pdf.text(invoiceData.customerEmail, 20, 105);
        if (invoiceData.cardholderName) {
            pdf.text(`Card: ${invoiceData.cardholderName}`, 20, 115);
        }

        // Line separator
        pdf.setLineWidth(0.5);
        pdf.line(20, 125, 190, 125);
        
        // Table header
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text('DESCRIPTION', 20, 140);
        pdf.text('AMOUNT', 150, 140);
        pdf.text('STATUS', 120, 140);
        
        // Table content
        pdf.setFont('helvetica', 'normal');
        pdf.text(invoiceData.description, 20, 155);
        pdf.text(invoiceData.amount, 150, 155);
        
        // Status with color
        if (transaction.status === 'COMPLETED') {
            pdf.setTextColor(34, 197, 94); // Green
        } else if (transaction.status === 'FAILED') {
            pdf.setTextColor(239, 68, 68); // Red
        } else {
            pdf.setTextColor(251, 191, 36); // Yellow
        }
        pdf.text(invoiceData.status, 120, 155);
        
        // Reset color
        pdf.setTextColor(0, 0, 0);
        
        // Line separator
        pdf.line(20, 165, 190, 165);
        
        // Total section
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.text('TOTAL:', 120, 180);
        pdf.text(invoiceData.amount, 150, 180);
        
        // Payment details
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text(`Payment ID: ${invoiceData.paymentId}`, 20, 200);
        
        // Footer
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(12);
        pdf.text('Thank you for your business!', 20, 230);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128); // Gray
        pdf.text('This is an automated invoice generated by The Deep Wave payment system.', 20, 250);
        
        // Save the PDF
        pdf.save(`thedeepwave.com-invoice-${transaction.id}.pdf`);
    };

    const handleVerifyEmail = async (e) => {
        e.preventDefault();
        if (verifyCode.trim().length !== 4) return;
        setVerifying(true);
        setVerifyResult(null);
        try {
            const res = await api.post('/auth/verify-email', { code: verifyCode.trim() });
            setVerifyResult({ success: res.data.success, message: res.data.message });
            if (res.data.success) {
                setVerifyCode('');
                fetchAccountData(); // refresh to show verified status
            }
        } catch (err) {
            setVerifyResult({ success: false, message: err.response?.data?.message || 'Verification failed.' });
        } finally {
            setVerifying(false);
        }
    };

    const handleResendCode = async () => {
        setResending(true);
        setResendResult(null);
        try {
            const res = await api.post('/auth/resend-verification');
            setResendResult({ success: res.data.success, message: res.data.message });
        } catch (err) {
            setResendResult({ success: false, message: err.response?.data?.message || 'Failed to resend code.' });
        } finally {
            setResending(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
                    <p className="text-gray-400">Loading account information...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-400 mb-4">{error}</p>
                    <button 
                        onClick={fetchAccountData}
                        className="btn-primary"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900">
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">Account Overview</h1>
                    <p className="text-gray-400">Manage your account information, subscriptions, and billing history</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Account Information */}
                    <div className="lg:col-span-1">
                        <div className="glass-card p-6">
                            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                                <User className="w-5 h-5" />
                                Account Information
                            </h2>
                            
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    {user?.profile?.profilePictureUrl ? (
                                        <img
                                            src={user.profile.profilePictureUrl}
                                            alt="Profile"
                                            width="48"
                                            height="48"
                                            className="w-12 h-12 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                                            <span className="text-2xl leading-none">🌊</span>
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-semibold text-white">{user?.profile?.displayName || 'User'}</p>
                                        <p className="text-sm text-gray-400">@{user?.profile?.username}</p>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-white/10 space-y-3">
                                    <div className="flex items-center gap-3 text-sm">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                        <span className="text-gray-300">{user?.email}</span>
                                    </div>
                                    
                                    {accountData?.phone && (
                                        <div className="flex items-center gap-3 text-sm">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <span className="text-gray-300">{accountData.phone}</span>
                                        </div>
                                    )}
                                    
                                    {accountData?.location && (
                                        <div className="flex items-center gap-3 text-sm">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                            <span className="text-gray-300">{accountData.location}</span>
                                        </div>
                                    )}
                                    
                                    <div className="flex items-center gap-3 text-sm">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <span className="text-gray-300">
                                            Member since {formatDate(user?.created_at || new Date())}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Email Verification Card */}
                        <div className={`glass-card p-6 border ${accountData?.email_verified ? 'border-green-500/30' : 'border-yellow-500/30'}`}>
                            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                {accountData?.email_verified
                                    ? <ShieldCheck className="w-5 h-5 text-green-400" />
                                    : <ShieldAlert className="w-5 h-5 text-yellow-400" />}
                                Email Verification
                            </h2>

                            {accountData?.email_verified ? (
                                <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-green-400 font-medium text-sm">Email verified</p>
                                        <p className="text-gray-500 text-xs">{user?.email}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-400">
                                        Verify your email address to earn your <span className="text-green-400 font-medium">Email Verified</span> badge.
                                        Check your inbox for a 4-digit code — or request a new one below.
                                    </p>

                                    <form onSubmit={handleVerifyEmail} className="flex gap-2">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={4}
                                            value={verifyCode}
                                            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                            placeholder="0000"
                                            className="w-28 bg-gray-800 border border-white/10 rounded-lg px-3 py-2.5 text-white text-center text-lg font-mono tracking-widest focus:outline-none focus:border-blue-500"
                                        />
                                        <button
                                            type="submit"
                                            disabled={verifying || verifyCode.length !== 4}
                                            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors text-sm"
                                        >
                                            {verifying ? <Loader className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                                            {verifying ? 'Verifying…' : 'Verify'}
                                        </button>
                                    </form>

                                    {verifyResult && (
                                        <p className={`text-sm ${verifyResult.success ? 'text-green-400' : 'text-red-400'}`}>
                                            {verifyResult.message}
                                        </p>
                                    )}

                                    <div className="pt-2 border-t border-white/10">
                                        <button
                                            onClick={handleResendCode}
                                            disabled={resending}
                                            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                                        >
                                            <RefreshCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
                                            {resending ? 'Sending…' : 'Send a new code'}
                                        </button>
                                        {resendResult && (
                                            <p className={`text-xs mt-1.5 ${resendResult.success ? 'text-green-400' : 'text-red-400'}`}>
                                                {resendResult.message}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Subscriptions and Transactions */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Active Subscriptions */}
                        <div className="glass-card p-6">
                            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                                <CreditCard className="w-5 h-5" />
                                Active Subscriptions
                            </h2>
                            
                            {(() => {
                                const visibleSubs = subscriptions.filter(s =>
                                    s.status === 'ACTIVE' ||
                                    s.status === 'PENDING' ||
                                    (s.status === 'CANCELED' && s.current_period_end && new Date(s.current_period_end) > new Date())
                                );
                                return visibleSubs.length > 0 ? (
                                <div className="space-y-4">
                                    {visibleSubs.map((subscription) => {
                                        const isCanceled = subscription.status === 'CANCELED';
                                        const canCancel = (subscription.status === 'ACTIVE' || subscription.status === 'PENDING') && !isCanceled;
                                        return (
                                        <div key={subscription.id} className="border border-white/10 rounded-lg p-4">
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h3 className="font-semibold text-white">{subscription.plan_name}</h3>
                                                    <p className="text-sm text-gray-400">{subscription.billing_interval}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold text-white">{formatCurrency(subscription.price)}</p>
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                                                            subscription.status === 'ACTIVE'
                                                                ? 'bg-green-500/20 text-green-400'
                                                                : isCanceled
                                                                ? 'bg-red-500/20 text-red-400'
                                                                : 'bg-yellow-500/20 text-yellow-400'
                                                        }`}>
                                                            {isCanceled ? 'Cancelled · Closes ' + formatDate(subscription.current_period_end) : subscription.status}
                                                        </span>
                                                        {canCancel && (
                                                            <button
                                                                onClick={() => handleDeactivateSubscription(subscription.id)}
                                                                disabled={deactivatingSubscription === subscription.id}
                                                                className="p-1 text-gray-500 hover:text-gray-300 hover:bg-white/5 rounded transition-all duration-200 disabled:opacity-50"
                                                                title="Cancel subscription"
                                                            >
                                                                {deactivatingSubscription === subscription.id ? (
                                                                    <Loader className="w-3 h-3 animate-spin" />
                                                                ) : (
                                                                    <X className="w-3 h-3" />
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <p className="text-gray-400">{isCanceled ? 'Access until' : 'Next billing date'}</p>
                                                    <p className="text-white">{formatDate(isCanceled ? subscription.current_period_end : subscription.next_billing_date)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400">Period ends</p>
                                                    <p className="text-white">{formatDate(subscription.current_period_end)}</p>
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                                ) : (null);
                            })()}
                            {subscriptions.filter(s =>
                                s.status === 'ACTIVE' ||
                                s.status === 'PENDING' ||
                                (s.status === 'CANCELED' && s.current_period_end && new Date(s.current_period_end) > new Date())
                            ).length === 0 && (
                                <div className="text-center py-8">
                                    <CreditCard className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                                    <p className="text-gray-400 mb-4">No active subscriptions</p>
                                    <a href="/subscriptions" className="btn-primary">
                                        View Plans
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Recent Transactions */}
                        <div className="glass-card p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                                    <Download className="w-5 h-5" />
                                    Recent Transactions
                                </h2>
                                {transactions.length > 0 && (
                                    <button className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
                                        <ExternalLink className="w-4 h-4" />
                                        View All
                                    </button>
                                )}
                            </div>
                            
                            {transactions.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-white/10">
                                                <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Date</th>
                                                <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Description</th>
                                                <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Amount</th>
                                                <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Status</th>
                                                <th className="text-right py-3 px-2 text-sm font-medium text-gray-400">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.slice(0, 5).map((transaction) => (
                                                <tr key={transaction.id} className="border-b border-white/5">
                                                    <td className="py-3 px-2 text-sm text-gray-300">
                                                        {formatDate(transaction.created_at)}
                                                    </td>
                                                    <td className="py-3 px-2 text-sm text-white">
                                                        {transaction.plan_name || 'Payment'}
                                                    </td>
                                                    <td className="py-3 px-2 text-sm text-white">
                                                        {formatCurrency(transaction.amount)}
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                                                            transaction.status === 'COMPLETED' 
                                                                ? 'bg-green-500/20 text-green-400'
                                                                : transaction.status === 'PENDING'
                                                                ? 'bg-yellow-500/20 text-yellow-400'
                                                                : 'bg-red-500/20 text-red-400'
                                                        }`}>
                                                            {transaction.status}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-2 text-right">
                                                        <div className="flex items-center gap-2 justify-end">
                                                            <button 
                                                                onClick={() => handleViewTransactionDetails(transaction)}
                                                                className="text-blue-400 hover:text-blue-300 text-sm"
                                                                title="View Details"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </button>
                                                            <button 
                                                                onClick={() => handleDownloadInvoice(transaction)}
                                                                className="text-green-400 hover:text-green-300 text-sm"
                                                                title="Download Invoice"
                                                            >
                                                                <Download className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <Download className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                                    <p className="text-gray-400">No transactions yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Transaction Details Modal */}
            {showTransactionModal && selectedTransaction && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
                    <div className="glass-card max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white">Transaction Details</h3>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => handleDownloadInvoice(selectedTransaction)}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-green-600/20 text-green-400 hover:bg-green-600/30 rounded transition-colors"
                                    title="Download Invoice"
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </button>
                                <button
                                    onClick={() => {
                                        setShowTransactionModal(false);
                                        setSelectedTransaction(null);
                                        setTransactionDetails(null);
                                    }}
                                    className="text-gray-400 hover:text-white"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {loadingTransactionDetails ? (
                            <div className="text-center py-8">
                                <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
                                <p className="text-gray-400">Loading transaction details...</p>
                            </div>
                        ) : transactionDetails ? (
                            <div className="space-y-6">
                                {/* Payment Information */}
                                <div>
                                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-blue-400" />
                                        Payment Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-800/50 rounded-lg">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Payment ID</label>
                                            <p className="text-white font-mono">{transactionDetails.id}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Square Payment ID</label>
                                            <p className="text-white font-mono">{transactionDetails.square_payment_id}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Amount</label>
                                            <p className="text-white text-lg font-semibold">
                                                {formatCurrency(transactionDetails.amount)}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Status</label>
                                            <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${
                                                transactionDetails.status === 'COMPLETED' 
                                                    ? 'bg-green-600/20 text-green-400'
                                                    : transactionDetails.status === 'FAILED'
                                                    ? 'bg-red-600/20 text-red-400'
                                                    : 'bg-yellow-600/20 text-yellow-400'
                                            }`}>
                                                {transactionDetails.status}
                                            </span>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Date</label>
                                            <p className="text-white">{formatDate(transactionDetails.created_at)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Processed At</label>
                                            <p className="text-white">
                                                {transactionDetails.processed_at && transactionDetails.processed_at !== 'Invalid Date' ? formatDateTime(transactionDetails.processed_at) : 'Pending'}
                                            </p>
                                        </div>
                                        {transactionDetails.plan_name && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400">Plan</label>
                                                <p className="text-white">{transactionDetails.plan_name}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Customer Information */}
                                <div>
                                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                        <Users className="w-5 h-5 text-green-400" />
                                        Customer Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-800/50 rounded-lg">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Email</label>
                                            <p className="text-white">{user?.email}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Account Name</label>
                                            <p className="text-white">
                                                {user?.profile?.displayName || user?.profile?.username || 'N/A'}
                                            </p>
                                        </div>
                                        {transactionDetails.cardholder_name && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400">Name on Card</label>
                                                <p className="text-white">{transactionDetails.cardholder_name}</p>
                                            </div>
                                        )}
                                        {transactionDetails.plan_name && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400">Plan</label>
                                                <p className="text-white">{transactionDetails.plan_name}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-red-400">Failed to load transaction details</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Account;